// ==================== FILE: src/components/FlowBuilder/FlowHeader.jsx ====================
import React from 'react';
import {
    Box,
    Flex,
    Input,
    Button,

    HStack,

    IconButton,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Spinner,
    Tag,
    Text,
    Tooltip,
    useToast,
} from '@chakra-ui/react';
import {
    FiArrowLeft,
    FiSave,
    FiMoreVertical,
    FiCopy,
    FiArchive,
    FiEdit2,
    FiCheck,
    FiX,

} from 'react-icons/fi';
import { getApiHeaders } from '../../utils/DefaultFunction';
import API from '../../helpers/axiosInstance';
import { useNavigate, useParams } from 'react-router-dom';


export default function FlowHeader({
    campaignName,
    setCampaignName,
    flowStatus,

    onBack,
    onSave,
    onSaveAndContinue,

    isSaving = false,
    isEdit = false,
}) {
    const toast = useToast();
    const [loading, setLoading] = React.useState(false);
    const [duplicating, setDuplicating] = React.useState(false);
    const { id } = useParams();
    const navigate = useNavigate();

    // ── flow name: read-only until the edit button is pressed ──────────────
    const [isEditingName, setIsEditingName] = React.useState(false);
    // edited in a draft so Cancel/Escape can put the old name back
    const [nameDraft, setNameDraft] = React.useState(campaignName);
    const nameRef = React.useRef(null);
    const [isNameTruncated, setIsNameTruncated] = React.useState(false);

    // In edit mode the flow loads after this mounts, so keep the draft in step with the
    // real name — but never while the user is typing into it.
    React.useEffect(() => {
        if (!isEditingName) setNameDraft(campaignName);
    }, [campaignName, isEditingName]);

    // Measure rather than guess a character count: the tooltip should appear only when
    // the name is actually clipped, at whatever width the header ends up.
    React.useEffect(() => {
        const el = nameRef.current;
        if (!el) return;
        setIsNameTruncated(el.scrollWidth > el.clientWidth + 1);
    }, [campaignName, isEditingName]);

    const startEditName = () => {
        setNameDraft(campaignName);
        setIsEditingName(true);
    };

    const commitName = () => {
        const next = (nameDraft || '').trim();
        // an empty name would leave the header blank and fail validation on save,
        // so treat "cleared and confirmed" as no change
        if (next) setCampaignName(next);
        else setNameDraft(campaignName);
        setIsEditingName(false);
    };

    const cancelEditName = () => {
        setNameDraft(campaignName);
        setIsEditingName(false);
    };
    const getStatusColor = () => {
        switch (flowStatus) {
            case 'active':
                return '#00a63e';
            case 'draft':
                return 'var(--brand-primary)';

            default:
                return 'gray';
        }
    };

    const handleDuplicate = async () => {
        setDuplicating(true);
        try {
            const response = await API.get(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/duplicate?id=${id}`,
                { headers: getApiHeaders() }
            );
            if (response.data.status) {
                toast({
                    title: 'Flow duplicated',
                    description: 'Flow duplicated successfully',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
                const newId = response.data.data?._id || response.data.data?.id;
                if (newId) navigate(`/flow-builder/${newId}`);
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to duplicate flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setDuplicating(false);
        }
    };

    const handleToggleStatus = async () => {
        setLoading(true);
        try {

            const response = await API.post(
                `${process.env.REACT_APP_FLOW_API_URL}/flow/update-status`,
                { status: "archive", flow_id: id },
                { headers: getApiHeaders() }
            );
            setLoading(false);
            if (response.status === 200) {

                toast({
                    title: 'Flow updated',
                    description: `Flow archived successfully`,
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            }


        } catch (error) {
            console.error('Error updating flow:', error);
            toast({
                title: 'Error',
                description: error.response?.data?.message || error.message || 'Failed to update flow',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            bg="white"
            borderBottom="1px"
            borderColor="gray.200"
            px={6}
            py={3}
            boxShadow="sm"
        >
            <Flex justify="space-between" align="center">
                {/* Left Section */}
                <HStack spacing={4}>
                    <IconButton
                        icon={<FiArrowLeft />}
                        variant="ghost"
                        onClick={onBack}
                        aria-label="Go back"
                        size="sm"
                    />
                    {isEditingName ? (
                        <HStack spacing={1}>
                            <Input
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') commitName();
                                    if (e.key === 'Escape') cancelEditName();
                                }}
                                // clicking away is a confirm; the buttons below suppress
                                // this with onMouseDown so Cancel is not swallowed by it
                                onBlur={commitName}
                                autoFocus
                                fontSize="lg"
                                fontWeight="semibold"
                                size="sm"
                                placeholder="Enter campaign name"
                                w="360px"
                                maxW="50vw"
                            />
                            <IconButton
                                icon={<FiCheck />}
                                aria-label="Save name"
                                size="sm"
                                variant="ghost"
                                colorScheme="green"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={commitName}
                            />
                            <IconButton
                                icon={<FiX />}
                                aria-label="Cancel renaming"
                                size="sm"
                                variant="ghost"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={cancelEditName}
                            />
                        </HStack>
                    ) : (
                        <HStack spacing={1} minW={0}>
                            {/* Tooltip only when the name is clipped, so it does not just
                                repeat what is already fully visible. */}
                            <Tooltip
                                label={campaignName}
                                placement="bottom-start"
                                openDelay={300}
                                isDisabled={!isNameTruncated}
                                hasArrow
                            >
                                <Text
                                    ref={nameRef}
                                    fontSize="lg"
                                    fontWeight="semibold"
                                    noOfLines={1}
                                    maxW="400px"
                                    color={campaignName ? 'inherit' : 'gray.400'}
                                    cursor="default"
                                >
                                    {campaignName || 'Enter campaign name'}
                                </Text>
                            </Tooltip>
                            <IconButton
                                icon={<FiEdit2 />}
                                aria-label="Edit flow name"
                                title="Edit flow name"
                                size="xs"
                                variant="ghost"
                                onClick={startEditName}
                            />
                        </HStack>
                    )}

                </HStack>



                {/* Right Section - Actions */}
                <HStack spacing={3}>
                    <Tag bg={getStatusColor()} size={"sm"} color="white" >
                        {flowStatus.toUpperCase()}
                    </Tag>


                    <Button
                        size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.700"
                        leftIcon={<FiSave />}
                        onClick={onSaveAndContinue}
                        isLoading={isSaving}
                        loadingText="Saving..."
                    >
                        Save & Continue
                    </Button>
                    <Button
                         size="sm"
                        variant="outline"
                        borderColor="gray.300"
                        color="gray.700"
                        leftIcon={isSaving ? <Spinner size="xs" /> : <FiSave />}
                        onClick={onSave}
                        isLoading={isSaving}
                        loadingText={"Saving..."}
                    >
                        Save And Close
                    </Button>
                    {loading ? <Spinner size="xs" /> : null}
                    {isEdit && (
                        <Menu>
                            <MenuButton
                                as={IconButton}
                                icon={<FiMoreVertical />}
                                variant="ghost"
                                size="sm"
                                aria-label="More options"
                            />
                            <MenuList>
                                <MenuItem icon={duplicating ? <Spinner size="xs" /> : <FiCopy />} onClick={handleDuplicate} isDisabled={duplicating}>Duplicate Flow</MenuItem>
                                <MenuItem icon={<FiArchive />} color="red.500" onClick={handleToggleStatus}>
                                    Archive Flow
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    )}

                </HStack>
            </Flex>
        </Box>
    );
}